import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
    LayoutDashboard,
    Store,
    FileImage,
    SlidersHorizontal,
    Server,
    Container,
    Video,
    Shield,
    Mail,
    ScrollText,
    Menu,
    Crosshair,
    ClipboardCheck,
    HelpCircle,
    ChevronDown,
    ChevronRight,
    CircleDot,
    CheckCircle2,
    ShieldCheck,
    ShieldAlert,
    Activity,
    Droplets,
    Settings,
    Wrench,
    ScanLine,
    Package,
    PackageSearch,
    Tag,
    Users,
    Cable,
    FileBarChart,
    BarChart3,
    Refrigerator,
    CalendarClock,
    HardHat,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

import usePageTracking from "../hooks/usePageTracking";

function Layout({ children }) {
    const { instance, accounts } = useMsal();
    const { user, hasPermission } = useAuth();
    const location = useLocation();
    const account = accounts[0];
    const [menuOpen, setMenuOpen] = useState(false);
    const [auditExpanded, setAuditExpanded] = useState(true);
    const [dipsExpanded, setDipsExpanded] = useState(true);
    const [assetsExpanded, setAssetsExpanded] = useState(true);
    const [configExpanded, setConfigExpanded] = useState(false);
    const [systemExpanded, setSystemExpanded] = useState(false);
    const [userPhoto, setUserPhoto] = useState(null);

    // Track page views
    usePageTracking();

    // Fetch Entra profile photo
    useEffect(() => {
        if (!account) return;
        (async () => {
            try {
                const tokenResp = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account,
                });
                const resp = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
                    headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
                });
                if (resp.ok) {
                    const blob = await resp.blob();
                    setUserPhoto(URL.createObjectURL(blob));
                }
            } catch { /* no photo available */ }
        })();
    }, [account, instance]);

    // Top-level nav items (always visible)
    const topNav = [
        { to: "/", icon: LayoutDashboard, label: "Dashboard", permission: "pages.dashboard", helpSlug: "dashboard" },
        { to: "/stores", icon: Store, label: "Stores", permission: "pages.stores", helpSlug: "stores" },
        { to: "/video-clips", icon: Video, label: "Video Clips", permission: "videos.view", helpSlug: "video-clips" },
        { to: "/contacts", icon: Users, label: "Contacts", permission: "pages.contacts", helpSlug: "contacts" },
        { to: "/fridge-survey", icon: Refrigerator, label: "Fridge Survey", permission: "pages.stores" },
    ];

    // Audit Reviews submenu
    const auditNav = [
        { to: "/monitoring", icon: Activity, label: "Monitoring", permission: "pages.monitoring", helpSlug: "monitoring" },
        { to: "/reviews", icon: ClipboardCheck, label: "Review Scans", permission: "pages.reports", helpSlug: "reviews" },
        { to: "/issues?status=open", icon: CircleDot, label: "Open Issues", permission: "pages.issues", helpSlug: "issues" },
        { to: "/issues?status=actioned", icon: ShieldCheck, label: "Verify Issues", permission: "pages.issues", helpSlug: "issues" },
        { to: "/issues?status=resolved", icon: CheckCircle2, label: "Closed Issues", permission: "pages.issues", helpSlug: "issues" },
        { to: "/reports/summary", icon: FileBarChart, label: "MTD Reports", permission: "pages.reports", helpSlug: "reports" },
        { to: "/reports/ops", icon: BarChart3, label: "Ops Overview", permission: "pages.reports", helpSlug: "reports" },
        { to: "/posfraud", icon: ShieldAlert, label: "POS Fraud", permission: "pages.posfraud", helpSlug: "posfraud" },
        { to: "/fraud-report", icon: FileBarChart, label: "Fraud Report", permission: "pages.fraud_report", helpSlug: "posfraud" },
        { to: "/dip-safety", icon: HardHat, label: "Dip Safety", permission: "pages.dip_safety" },
    ];

    // Dip Monitoring submenu
    const dipsNav = [
        { to: "/dip-form", icon: ClipboardCheck, label: "Submit Dip Report", helpSlug: "dip-form" },
        { to: "/dip-evidence", icon: Video, label: "Dip Evidence", permission: "pages.dip_evidence", helpSlug: "dip-evidence" },
    ];

    // Assets submenu
    const assetsNav = [
        { to: "/assets", icon: ScanLine, label: "Scan Assets", permission: "pages.assets", helpSlug: "assets" },
        { to: "/asset-review", icon: PackageSearch, label: "Review Requests", permission: "assets.review", helpSlug: "asset-review" },
    ];

    // Configuration submenu
    const configNav = [
        { to: "/dips", icon: Droplets, label: "Dip Configuration", permission: "pages.dips", helpSlug: "dips" },
        { to: "/infrastructure", icon: Server, label: "Infrastructure", permission: "pages.infrastructure", helpSlug: "infrastructure" },
        { to: "/planograms", icon: FileImage, label: "Planograms", permission: "pages.planograms", helpSlug: "planograms" },
        { to: "/presets", icon: SlidersHorizontal, label: "Presets", permission: "pages.presets", helpSlug: "presets" },
        { to: "/onboarding", icon: Crosshair, label: "Onboarding", permission: "pages.onboarding", helpSlug: "onboarding" },
        { to: "/asset-types", icon: Tag, label: "Asset Types", permission: "assets.review", helpSlug: "asset-types" },
        { to: "/fixture-schedules", icon: CalendarClock, label: "Scan Schedules", permission: "pages.schedules", helpSlug: "fixture-schedules" },
        { to: "/nvr-guide", icon: Cable, label: "NVR Connection", permission: "pages.infrastructure", helpSlug: "nvr-guide" },
    ];

    // System submenu
    const systemNav = [
        { to: "/workers", icon: Container, label: "Azure Workers", permission: "pages.infrastructure", helpSlug: "workers" },
        { to: "/admin", icon: Shield, label: "Admin", permission: "admin.users", helpSlug: "admin" },
        { to: "/email-log", icon: Mail, label: "Email Log", permission: "pages.email", helpSlug: "email-log" },
        { to: "/activity-log", icon: ScrollText, label: "Activity Log", permission: "admin.audit", helpSlug: "activity-log" },
    ];

    // For backward compat — allNav used for help button lookup
    const allNav = [...topNav, ...auditNav, ...dipsNav, ...assetsNav, ...configNav, ...systemNav];

    // Filter by permission
    const topNavFiltered = topNav.filter((item) => !item.permission || hasPermission(item.permission));
    const auditNavFiltered = auditNav.filter((item) => !item.permission || hasPermission(item.permission));
    const dipsNavFiltered = dipsNav.filter((item) => !item.permission || hasPermission(item.permission));
    const assetsNavFiltered = assetsNav.filter((item) => !item.permission || hasPermission(item.permission));
    const configNavFiltered = configNav.filter((item) => !item.permission || hasPermission(item.permission));
    const systemNavFiltered = systemNav.filter((item) => !item.permission || hasPermission(item.permission));

    // Active states for collapsible groups
    const isAuditActive = location.pathname.startsWith("/reviews") || location.pathname.startsWith("/issues") || location.pathname.startsWith("/monitoring") || location.pathname.startsWith("/reports") || location.pathname.startsWith("/posfraud") || location.pathname.startsWith("/dip-safety");
    const isDipsActive = location.pathname.startsWith("/dip-evidence") || location.pathname.startsWith("/dip-form");
    const isAssetsActive = location.pathname.startsWith("/assets") || location.pathname.startsWith("/asset-review");
    const isConfigActive = location.pathname.startsWith("/dips") || location.pathname.startsWith("/infrastructure") || location.pathname.startsWith("/planograms") || location.pathname.startsWith("/presets") || location.pathname.startsWith("/onboarding") || location.pathname.startsWith("/asset-types") || location.pathname.startsWith("/fixture-schedules") || location.pathname.startsWith("/nvr-guide");
    const isSystemActive = location.pathname.startsWith("/admin") || location.pathname.startsWith("/email-log") || location.pathname.startsWith("/activity-log") || location.pathname.startsWith("/workers");

    const handleLogout = () => {
        instance.logoutRedirect({ postLogoutRedirectUri: "/" });
    };

    // Display role from RBAC data
    const displayRole = user?.roles?.[0]?.name || "User";

    return (
        <div className="layout">
            {/* Mobile hamburger button */}
            <button
                className="mobile-menu-btn"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
            >
                <Menu size={24} />
            </button>

            {/* Mobile overlay */}
            {menuOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setMenuOpen(false)}
                />
            )}

            <aside className={`sidebar ${menuOpen ? "sidebar-open" : ""}`}>
                <div className="sidebar-brand">
                    <div className="sidebar-brand-icon">SA</div>
                    <div>
                        <h1>Store Audit</h1>
                        <span>United Petroleum</span>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {/* Top-level links */}
                    {topNavFiltered.map(({ to, icon: Icon, label }) => (
                        <Link
                            key={to}
                            to={to}
                            className={`sidebar-link ${(to === "/" ? location.pathname === "/" : location.pathname.startsWith(to)) ? "active" : ""}`}
                            onClick={() => setMenuOpen(false)}
                        >
                            <Icon size={18} />
                            {label}
                        </Link>
                    ))}

                    {/* Audit Reviews group */}
                    {auditNavFiltered.length > 0 && (
                        <>
                            <button
                                className={`sidebar-section-toggle ${isAuditActive ? "active" : ""}`}
                                onClick={() => setAuditExpanded(!auditExpanded)}
                            >
                                <ClipboardCheck size={18} />
                                Audit Reviews
                                {auditExpanded ? <ChevronDown size={14} className="sidebar-chevron" /> : <ChevronRight size={14} className="sidebar-chevron" />}
                            </button>
                            {auditExpanded && auditNavFiltered.map(({ to: subTo, icon: SubIcon, label: subLabel }) => {
                                const [path, qs] = subTo.split("?");
                                const isActive = location.pathname === path && (!qs || location.search === `?${qs}`);
                                return (
                                    <Link key={subTo} to={subTo}
                                        className={`sidebar-link sidebar-sublink ${isActive ? "active" : ""}`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <SubIcon size={16} />{subLabel}
                                    </Link>
                                );
                            })}
                        </>
                    )}

                    {/* Dip Evidence group */}
                    {dipsNavFiltered.length > 0 && (
                        <>
                            <button
                                className={`sidebar-section-toggle ${isDipsActive ? "active" : ""}`}
                                onClick={() => setDipsExpanded(!dipsExpanded)}
                            >
                                <Droplets size={18} />
                                Dip Evidence
                                {dipsExpanded ? <ChevronDown size={14} className="sidebar-chevron" /> : <ChevronRight size={14} className="sidebar-chevron" />}
                            </button>
                            {dipsExpanded && dipsNavFiltered.map(({ to: subTo, icon: SubIcon, label: subLabel }) => {
                                const isActive = location.pathname.startsWith(subTo);
                                return (
                                    <Link key={subTo} to={subTo}
                                        className={`sidebar-link sidebar-sublink ${isActive ? "active" : ""}`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <SubIcon size={16} />{subLabel}
                                    </Link>
                                );
                            })}
                        </>
                    )}

                    {/* Assets group */}
                    {assetsNavFiltered.length > 0 && (
                        <>
                            <button
                                className={`sidebar-section-toggle ${isAssetsActive ? "active" : ""}`}
                                onClick={() => setAssetsExpanded(!assetsExpanded)}
                            >
                                <Package size={18} />
                                Assets
                                {assetsExpanded ? <ChevronDown size={14} className="sidebar-chevron" /> : <ChevronRight size={14} className="sidebar-chevron" />}
                            </button>
                            {assetsExpanded && assetsNavFiltered.map(({ to: subTo, icon: SubIcon, label: subLabel }) => {
                                const isActive = location.pathname.startsWith(subTo);
                                return (
                                    <Link key={subTo} to={subTo}
                                        className={`sidebar-link sidebar-sublink ${isActive ? "active" : ""}`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <SubIcon size={16} />{subLabel}
                                    </Link>
                                );
                            })}
                        </>
                    )}

                    {/* Configuration group */}
                    {configNavFiltered.length > 0 && (
                        <>
                            <button
                                className={`sidebar-section-toggle ${isConfigActive ? "active" : ""}`}
                                onClick={() => setConfigExpanded(!configExpanded)}
                            >
                                <Wrench size={18} />
                                Configuration
                                {configExpanded ? <ChevronDown size={14} className="sidebar-chevron" /> : <ChevronRight size={14} className="sidebar-chevron" />}
                            </button>
                            {configExpanded && configNavFiltered.map(({ to: subTo, icon: SubIcon, label: subLabel }) => {
                                const isActive = location.pathname.startsWith(subTo);
                                return (
                                    <Link key={subTo} to={subTo}
                                        className={`sidebar-link sidebar-sublink ${isActive ? "active" : ""}`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <SubIcon size={16} />{subLabel}
                                    </Link>
                                );
                            })}
                        </>
                    )}

                    {/* System group */}
                    {systemNavFiltered.length > 0 && (
                        <>
                            <button
                                className={`sidebar-section-toggle ${isSystemActive ? "active" : ""}`}
                                onClick={() => setSystemExpanded(!systemExpanded)}
                            >
                                <Settings size={18} />
                                System
                                {systemExpanded ? <ChevronDown size={14} className="sidebar-chevron" /> : <ChevronRight size={14} className="sidebar-chevron" />}
                            </button>
                            {systemExpanded && systemNavFiltered.map(({ to: subTo, icon: SubIcon, label: subLabel }) => {
                                const isActive = location.pathname.startsWith(subTo);
                                return (
                                    <Link key={subTo} to={subTo}
                                        className={`sidebar-link sidebar-sublink ${isActive ? "active" : ""}`}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <SubIcon size={16} />{subLabel}
                                    </Link>
                                );
                            })}
                        </>
                    )}
                </nav>

                <div className="sidebar-user">
                    <button className="sidebar-logout" onClick={handleLogout}>
                        Sign out
                    </button>
                </div>
            </aside>

            <main className="main-content">
                <div className="main-topbar">
                    {(() => {
                        const currentNav = allNav.find(n => {
                            const path = n.to.split("?")[0];
                            return path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
                        });
                        return currentNav?.helpSlug ? (
                            <Link to={`/help/${currentNav.helpSlug}`} className="help-btn" title="Page help">
                                <HelpCircle size={18} />
                            </Link>
                        ) : null;
                    })()}
                    <div className="user-thumb" title={`${account?.name || "User"}\n${displayRole}`}>
                        {userPhoto ? (
                            <img src={userPhoto} alt="" />
                        ) : (
                            <span className="user-thumb-initials">
                                {(account?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                            </span>
                        )}
                    </div>
                </div>
                {children}
            </main>
        </div>
    );
}

export default Layout;
